import { DataAccess } from "../../dataaccess/data.access";
import { BuildResult, EditPlanResponse, FormWorkflowPlan, PlannedForm, PlanResponse } from "./form.workflow.plan";

export class FormWorkflowBuilderService {
    private static readonly POLL_INTERVAL_MS = 3_000;
    private static readonly POLL_TIMEOUT_MS = 8 * 60 * 1_000;

    static async plan(messages: { role: string; content: string }[]): Promise<PlanResponse> {
        const { chatResponse: uuid } = await DataAccess.post<PlanResponse>("/ai/formWorkflow/plan.json", { messages });
        return FormWorkflowBuilderService.pollForPlan(uuid);
    }

    private static pollForPlan(uuid: string): Promise<PlanResponse> {
        const start = Date.now();
        return new Promise((resolve, reject) => {
            const poll = async () => {
                if (Date.now() - start > FormWorkflowBuilderService.POLL_TIMEOUT_MS) {
                    return reject(new Error("Planning timed out after 8 minutes. Please try again."));
                }
                try {
                    const response = await DataAccess.get<PlanResponse>("/ai/formWorkflow/plan/" + uuid + ".json");
                    if (!response) {
                        setTimeout(poll, FormWorkflowBuilderService.POLL_INTERVAL_MS);
                        return;
                    }
                    resolve(response);
                } catch (e: any) {
                    if (e?.status === 202) {
                        setTimeout(poll, FormWorkflowBuilderService.POLL_INTERVAL_MS);
                        return;
                    }
                    reject(e);
                }
            };
            poll();
        });
    }

    static async build(plan: FormWorkflowPlan): Promise<BuildResult> {
        return DataAccess.post<BuildResult>("/ai/formWorkflow/build.json", { plan });
    }

    static async editPlan(formId: number, messages: { role: string; content: string }[]): Promise<EditPlanResponse> {
        const { chatResponse: uuid } = await DataAccess.post<EditPlanResponse>("/ai/form/editPlan.json", {
            ingestionPointId: formId,
            messages,
        });
        return FormWorkflowBuilderService.pollForEditPlan(uuid);
    }

    private static pollForEditPlan(uuid: string): Promise<EditPlanResponse> {
        const start = Date.now();
        return new Promise((resolve, reject) => {
            const poll = async () => {
                if (Date.now() - start > FormWorkflowBuilderService.POLL_TIMEOUT_MS) {
                    return reject(new Error("Planning timed out after 8 minutes. Please try again."));
                }
                try {
                    const response = await DataAccess.get<EditPlanResponse>("/ai/form/editPlan/" + uuid + ".json");
                    if (!response) {
                        setTimeout(poll, FormWorkflowBuilderService.POLL_INTERVAL_MS);
                        return;
                    }
                    resolve(response);
                } catch (caughtError: any) {
                    if (caughtError?.status === 202) {
                        setTimeout(poll, FormWorkflowBuilderService.POLL_INTERVAL_MS);
                        return;
                    }
                    reject(caughtError);
                }
            };
            poll();
        });
    }

    static async editBuild(formId: number, form: PlannedForm): Promise<BuildResult> {
        return DataAccess.post<BuildResult>("/ai/form/editBuild.json", { ingestionPointId: formId, form });
    }
}
